/**
 * Arthaus Product Detail (PDP) module
 * Phase: 4 (refactor)
 *
 * Exposes window.ArthausPDP with a re-callable init() so the frame-switcher
 * can re-bind the panel + gallery after an in-place product swap
 * (see assets/frame-switcher.js).
 *
 * Responsibilities:
 *   - Variant option selectors (Size / Frame Colour / Borders / Paper Type …)
 *     → resolve to a variant, update price, availability, hidden input, URL
 *   - Image gallery: thumbnail swap + lightbox
 *   - Ajax add-to-cart → dispatches `cart:updated`
 *
 * Document/window-level listeners (lightbox keyboard nav) are bound ONCE at
 * load; element-level listeners are (re)bound by init() and die with the DOM
 * they were attached to, so swapping is leak-free.
 */
window.ArthausPDP = (function () {
  'use strict';

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    return '$' + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  }

  // ── shared lightbox state (the lightbox element persists across swaps) ──
  var lightbox = null;
  var lightboxImg = null;
  var lb = { images: [], index: 0 };

  // Gallery API for the current DOM, set by initGallery() and consumed by
  // initConfig() to push the selected variant's image into slide 0.
  var galleryApi = null;

  function showLightboxImage() {
    var im = lb.images[lb.index];
    if (!im || !lightboxImg) return;
    lightboxImg.src = im.src;
    lightboxImg.alt = im.alt || '';
    if (im.width) lightboxImg.width = im.width;
    if (im.height) lightboxImg.height = im.height;
  }
  function openLightbox() {
    if (!lightbox) return;
    showLightboxImage();
    lightbox.hidden = false;
    // Apply flex via inline style (not a static class) so the `hidden`
    // attribute reliably hides it — Tailwind's `[hidden]:where(){display:none}`
    // reset has zero specificity and would otherwise lose to a `.flex` class.
    lightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }
  function closeLightbox() {
    if (!lightbox) return;
    lightbox.style.display = '';
    lightbox.hidden = true;
    document.body.style.overflow = '';
  }
  function navLightbox(dir) {
    if (!lb.images.length) return;
    lb.index = (lb.index + dir + lb.images.length) % lb.images.length;
    showLightboxImage();
  }
  function bindLightboxOnce() {
    lightbox = document.getElementById('product-lightbox');
    lightboxImg = document.getElementById('lightbox-image');
    if (!lightbox) return;
    var closeBtn = lightbox.querySelector('[data-lightbox-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
    var prev = lightbox.querySelector('[data-lightbox-prev]');
    if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); navLightbox(-1); });
    var next = lightbox.querySelector('[data-lightbox-next]');
    if (next) next.addEventListener('click', function (e) { e.stopPropagation(); navLightbox(1); });
    document.addEventListener('keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navLightbox(-1);
      if (e.key === 'ArrowRight') navLightbox(1);
    });
  }

  // ── Gallery (thumbnail swap + lightbox trigger) ────────────────────────
  function initGallery(scope) {
    var hero = scope && scope.querySelector ? scope.querySelector('[data-product-hero]') : null;
    if (!hero && scope && scope.matches && scope.matches('[data-product-hero]')) hero = scope;
    if (!hero) hero = document.querySelector('[data-product-hero]');
    if (!hero) return;

    var primaryImg = hero.querySelector('#product-primary-image');
    var thumbs = Array.prototype.slice.call(hero.querySelectorAll('[data-thumb-index]'));
    var images = [];

    if (thumbs.length) {
      thumbs.forEach(function (t) {
        images.push({
          src: t.getAttribute('data-thumb-src'),
          srcset: t.getAttribute('data-thumb-srcset'),
          alt: t.getAttribute('data-thumb-alt'),
          width: t.getAttribute('data-thumb-width'),
          height: t.getAttribute('data-thumb-height'),
          aspect: t.getAttribute('data-thumb-aspect')
        });
      });
    } else if (primaryImg) {
      images.push({
        src: primaryImg.getAttribute('src'),
        srcset: primaryImg.getAttribute('srcset'),
        alt: primaryImg.getAttribute('alt'),
        width: primaryImg.getAttribute('width'),
        height: primaryImg.getAttribute('height'),
        aspect: primaryImg.style.aspectRatio
      });
    }

    // Slide 0 is "variant-driven": when a variant with its own image is selected,
    // it overrides the default first slide (the template-composite); deselecting
    // (a variant without an image) reverts to images[0].
    var variantOverride = null;
    function slideAt(i) { return (i === 0 && variantOverride) ? variantOverride : images[i]; }

    var current = 0;
    function setImage(i) {
      var im = slideAt(i);
      if (!primaryImg || !im) return;
      current = i;
      primaryImg.src = im.src;
      primaryImg.srcset = im.srcset || '';
      primaryImg.alt = im.alt || '';
      if (im.width) primaryImg.width = im.width;
      if (im.height) primaryImg.height = im.height;
      if (im.aspect) primaryImg.style.aspectRatio = im.aspect;
      thumbs.forEach(function (t, j) {
        t.classList.toggle('border-charcoal', j === i);
        t.classList.toggle('border-transparent', j !== i);
      });
    }
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { setImage(i); });
    });

    var mainArea = hero.querySelector('[data-gallery-main]');
    if (mainArea) {
      mainArea.addEventListener('click', function () {
        lb.images = images.map(function (im, idx) { return (idx === 0 && variantOverride) ? variantOverride : im; });
        lb.index = current;
        openLightbox();
      });
    }

    // Exposed to initConfig: set/clear the variant image shown in slide 0.
    galleryApi = {
      setVariantImage: function (im) {
        variantOverride = im || null;
        setImage(0); // jump to + render the (now variant-specific) first slide
      }
    };
  }

  // ── Config panel (options → variant, price, add-to-cart) ───────────────
  function initConfig(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var configEl = root.querySelector('[data-product-config]');
    if (!configEl) return;

    var variants = [];
    var json = configEl.querySelector('[data-variants-json]');
    if (json) {
      try { variants = JSON.parse(json.textContent); } catch (e) { variants = []; }
    }

    // Optional variant → image map (from the gallery), keyed by variant id, used to
    // drive slide 0 when the shopper changes options. Absent → swap is a no-op.
    var variantImages = {};
    var vimgEl = (scope && scope.querySelector && scope.querySelector('[data-variant-images]')) ||
                 document.querySelector('[data-variant-images]');
    if (vimgEl) {
      try {
        (JSON.parse(vimgEl.textContent) || []).forEach(function (im) { if (im && im.id != null) variantImages[im.id] = im; });
      } catch (e) { variantImages = {}; }
    }

    var priceEl      = configEl.querySelector('[data-current-price]');
    var compareEl    = configEl.querySelector('[data-compare-price]');
    var variantInput = configEl.querySelector('[data-variant-input]');
    var addBtn       = configEl.querySelector('[data-add-to-cart-btn]');
    var addBtnText   = configEl.querySelector('[data-add-to-cart-text]');
    var form         = configEl.querySelector('[data-add-to-cart-form]');
    var groups       = Array.prototype.slice.call(configEl.querySelectorAll('[data-option-group]'));

    // selected[position] = value
    var selected = [];
    groups.forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10);
      var pressed = group.querySelector('[data-option-value][aria-pressed="true"]') ||
                    group.querySelector('[data-option-value]');
      if (pressed) selected[pos] = pressed.getAttribute('data-value');
    });

    function findVariant() {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var opts = v.options || [];
        var match = true;
        for (var j = 0; j < opts.length; j++) {
          if (selected[j + 1] !== opts[j]) { match = false; break; }
        }
        if (match && opts.length) return v;
      }
      // single-option / default-variant products
      return variants.length === 1 ? variants[0] : null;
    }

    // Selected appearance is CSS-driven via `aria-pressed:` Tailwind variants,
    // so toggling the attribute is enough — works for pills and colour swatches alike.
    function setPressed(btn, on) {
      btn.setAttribute('aria-pressed', String(on));
    }

    // Mirror the chosen value into the group's "selected value" label (colour swatches).
    function updateSelectedLabel(pos, value) {
      var label = configEl.querySelector('[data-selected-label][data-for-position="' + pos + '"]');
      if (label) label.textContent = value;
    }

    function refresh(isUser) {
      var v = findVariant();
      if (v) {
        if (variantInput) variantInput.value = v.id;
        // Variant-driven first image: on a shopper option change, show this
        // variant's image as slide 0 (revert to the template-composite if none).
        if (isUser && galleryApi) galleryApi.setVariantImage(variantImages[v.id] || null);
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (compareEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareEl.textContent = formatMoney(v.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
        if (addBtn) addBtn.disabled = !v.available;
        if (addBtnText) addBtnText.textContent = v.available ? 'Add to Cart' : 'Sold Out';
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState(history.state, '', url.toString());
        } catch (e) { /* no-op */ }
      } else {
        if (addBtn) addBtn.disabled = true;
        if (addBtnText) addBtnText.textContent = 'Unavailable';
      }
    }

    groups.forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10);
      group.querySelectorAll('[data-option-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          selected[pos] = btn.getAttribute('data-value');
          group.querySelectorAll('[data-option-value]').forEach(function (b) {
            setPressed(b, b === btn);
          });
          updateSelectedLabel(pos, btn.getAttribute('data-value'));
          refresh(true);
        });
      });
    });

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var id = variantInput ? variantInput.value : null;
        if (!id) return;
        if (addBtn) addBtn.disabled = true;
        if (addBtnText) addBtnText.textContent = 'Adding…';

        var body = { id: parseInt(id, 10), quantity: 1, properties: {} };
        var giftWrap = form.querySelector('[name="properties[Gift Wrap]"]');
        if (giftWrap && giftWrap.checked) body.properties['Gift Wrap'] = 'Yes';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (res) { if (!res.ok) throw new Error('Cart error'); return res.json(); })
          .then(function () {
            if (addBtnText) addBtnText.textContent = 'Added ✓';
            document.dispatchEvent(new CustomEvent('cart:updated'));
            if (window.ArthausCart && window.ArthausCart.openWithItem) {
              window.ArthausCart.openWithItem();
              setTimeout(function () {
                if (addBtn) addBtn.disabled = false;
                if (addBtnText) addBtnText.textContent = 'Add to Cart';
              }, 1200);
            } else {
              setTimeout(function () { window.location.href = '/cart'; }, 500);
            }
          })
          .catch(function () {
            if (addBtnText) addBtnText.textContent = 'Error — try again';
            if (addBtn) addBtn.disabled = false;
            setTimeout(function () { if (addBtnText) addBtnText.textContent = 'Add to Cart'; }, 2000);
          });
      });
    }

    refresh();
  }

  function init(scope) {
    initGallery(scope || document);
    initConfig(scope || document);
  }

  document.addEventListener('DOMContentLoaded', function () {
    bindLightboxOnce();
    init(document);
  });

  return { init: init, initConfig: initConfig, initGallery: initGallery };
})();
